import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import {
  addListEntry,
  changeListEntry,
  listQuery,
  setAbilities,
  type ListEntry,
  type ListKind,
} from '../api/abilities.ts';
import { rolesQuery, type CrewMember } from '../api/crew.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ CRW-03 at v1.9107, crew part 4E-2: what a person can do, on their crew record — the roles
// they can work, their skills, an employment type and a tier — and the tenant's lists of skills and
// employment types with the other crew settings. The eligible-crew filter (4E-3) reads them.

const TIERS = [1, 2, 3] as const;

/** "Tier 1 · Casual": what the crew list shows beside class. */
export function abilitiesLine(member: CrewMember, types: readonly ListEntry[] | undefined) {
  const type = types?.find((t) => t.id === member.employmentTypeId)?.name;
  return [member.tier ? `Tier ${member.tier}` : null, type].filter(Boolean).join(' · ');
}

/** A person's roles, skills, employment type and tier: read, or set with crew.manage. */
export function CanDo({ member, mayManage }: { member: CrewMember; mayManage: boolean }) {
  const queryClient = useQueryClient();
  const roles = useQuery(rolesQuery);
  const skills = useQuery(listQuery('skills'));
  const types = useQuery(listQuery('employment-types'));
  const [roleIds, setRoleIds] = useState<string[]>(member.roleIds);
  const [skillIds, setSkillIds] = useState<string[]>(member.skillIds);
  const [employmentTypeId, setEmploymentTypeId] = useState(member.employmentTypeId ?? '');
  const [tier, setTier] = useState(member.tier ? String(member.tier) : '');
  const [newSkill, setNewSkill] = useState('');
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      // A skill named here is added to the tenant's list first.
      const added = newSkill.trim() ? (await addListEntry('skills', newSkill.trim())).id : null;
      return setAbilities(member.id, {
        roleIds,
        skillIds: added ? [...skillIds, added] : skillIds,
        employmentTypeId: employmentTypeId || null,
        tier: tier ? Number(tier) : null,
      });
    },
    onSuccess: async (a) => {
      setSkillIds(a.skillIds);
      setNewSkill('');
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['skills'] });
      await queryClient.invalidateQueries({ queryKey: ['crew'] });
    },
  });
  const toggle = (list: string[], set: (v: string[]) => void, id: string, on: boolean) => {
    setSaved(false);
    set(on ? [...list, id] : list.filter((x) => x !== id));
  };
  const name = (list: readonly { id: string; name: string }[] | undefined, id: string) =>
    list?.find((x) => x.id === id)?.name ?? '';

  if (!mayManage) {
    const words = [
      member.roleIds.map((id) => name(roles.data, id)).join(', '),
      member.skillIds.map((id) => name(skills.data, id)).join(', '),
      abilitiesLine(member, types.data),
    ].filter(Boolean);
    return <p className="text-sm">{words.length ? words.join(' · ') : 'Nothing recorded.'}</p>;
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }
  const liveSkills = (skills.data ?? []).filter((s) => !s.retired || skillIds.includes(s.id));

  return (
    <form className="flex flex-col gap-3" onSubmit={submit}>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-xs text-ink-soft">Roles they can work</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {(roles.data ?? []).map((r) => (
            <label key={r.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={roleIds.includes(r.id)}
                onChange={(e) => toggle(roleIds, setRoleIds, r.id, e.currentTarget.checked)}
              />
              {r.name}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-xs text-ink-soft">Skills</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {liveSkills.map((s) => (
            <label key={s.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={skillIds.includes(s.id)}
                onChange={(e) => toggle(skillIds, setSkillIds, s.id, e.currentTarget.checked)}
              />
              {s.name}
            </label>
          ))}
        </div>
        <Field
          label="New skill"
          value={newSkill}
          onChange={(e) => {
            setSaved(false);
            setNewSkill(e.currentTarget.value);
          }}
        />
      </fieldset>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-ink-soft">Employment type</span>
          <select
            className="input"
            value={employmentTypeId}
            onChange={(e) => {
              setSaved(false);
              setEmploymentTypeId(e.currentTarget.value);
            }}
          >
            <option value="">—</option>
            {(types.data ?? [])
              .filter((t) => !t.retired || t.id === employmentTypeId)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-ink-soft">Tier</span>
          <select
            className="input"
            value={tier}
            onChange={(e) => {
              setSaved(false);
              setTier(e.currentTarget.value);
            }}
          >
            <option value="">—</option>
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
      </div>
      {save.isError ? (
        <p role="alert" className="alert">
          {save.error.message}
        </p>
      ) : null}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={save.isPending}>
          Save
        </Button>
        {saved ? (
          <span role="status" className="text-sm text-ink-soft">
            Saved
          </span>
        ) : null}
      </div>
    </form>
  );
}

const LISTS: Array<{ kind: ListKind; title: string; noun: string }> = [
  { kind: 'skills', title: 'Skills', noun: 'skill' },
  { kind: 'employment-types', title: 'Employment types', noun: 'employment type' },
];

/** Settings: the tenant's skills and employment types, each added and retired. */
export function AbilityLists({ editable }: { editable: boolean }) {
  return (
    <section className="flex flex-col gap-3" aria-labelledby="ability-lists">
      <h2 id="ability-lists" className="section-title">
        What crew can do
      </h2>
      <div className="grid gap-6 sm:grid-cols-2">
        {LISTS.map((l) => (
          <AbilityList key={l.kind} {...l} editable={editable} />
        ))}
      </div>
    </section>
  );
}

function AbilityList({
  kind,
  title,
  noun,
  editable,
}: {
  kind: ListKind;
  title: string;
  noun: string;
  editable: boolean;
}) {
  const queryClient = useQueryClient();
  const list = useQuery(listQuery(kind));
  const [name, setName] = useState('');
  const change = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [kind] }),
  });
  function add(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    change.mutate(async () => {
      await addListEntry(kind, name.trim());
      setName('');
    });
  }
  return (
    <div className="flex flex-col gap-2">
      <h3 className="font-heading text-sm font-extrabold">{title}</h3>
      {list.data?.length === 0 ? <p className="text-sm text-muted">None yet.</p> : null}
      <ul className="flex flex-col gap-1 text-sm">
        {(list.data ?? []).map((e) => (
          <li key={e.id} className={`flex items-center gap-2 ${e.retired ? 'text-muted' : ''}`}>
            <span className="flex-1">
              {e.name}
              {e.retired ? <span className="tag ml-2">Retired</span> : null}
            </span>
            {editable ? (
              <Button
                variant="quiet"
                className="small-btn"
                disabled={change.isPending}
                onClick={() =>
                  change.mutate(() => changeListEntry(kind, e.id, { retired: !e.retired }))
                }
              >
                {e.retired ? 'Restore' : 'Retire'}
                <span className="sr-only"> {e.name}</span>
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {editable ? (
        <form className="flex flex-wrap items-end gap-2" onSubmit={add}>
          <Field
            label={`New ${noun}`}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <Button type="submit" variant="quiet" disabled={change.isPending || !name.trim()}>
            Add
          </Button>
        </form>
      ) : null}
      {change.isError ? (
        <p role="alert" className="alert">
          {change.error.message}
        </p>
      ) : null}
    </div>
  );
}
